/**
 * Create by Bowen: 2019/12/1
 * Description:
 * Add entity/contact to relationship
 * ------ maintenance history ------
 * 2020/4/29 Bowen: When filterSource is no data, should show 'No Data': Has the ticket for kendo: when there is no data, the kendo don't
 * have the 'no data' template, need to use this workaround to handle this situation
 * 2020/07/22 Abner Sui added ViewContainerRef
 */

import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewContainerRef } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { AddRelationshipEntityDialogService } from './add-relationship-entity-dialog.service';
import { RelationshipTreeEvents } from '../relationship-tree.view-model';
import { EntityType } from '../../../tamalelibs/models/entity-type.model';
import { FilterPanelEvents, FilterPanelConfig, FilterPanelActions } from '../../../widgets/filter-item/filter-panel/filter-panel.view-model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { take, catchError } from 'rxjs/operators';
import { FilterMetadataType } from '../../../tamalelibs/components/filter-config';
import { DialogAction } from '../../../widgets/tam-dialog/tam-dialog.model';
import { RelationshipService } from '../../../tamalelibs/services/relationship.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';

@Component({
    selector: 'tam-add-relationship-entity-dialog',
    templateUrl: './add-relationship-entity-dialog.component.html',
    styleUrls: ['./add-relationship-entity-dialog.component.scss']
})
export class AddRelationshipEntityDialogComponent implements OnInit, OnDestroy {

    dialogTitle = null;
    rootNode = null;
    operation = null;
    metadataType = null;
    isReplaceWithCreatedEntity = false;
    isCreatWithReciprocalRel = true;
    isCreatWithReciprocalRelShow = false;
    isReplaceExistingShow = true;
    dialogOpened = false;
    addEntityPanelConfig: any;
    filterPanelList = [];
    filterConfig: FilterPanelConfig = new FilterPanelConfig();
    // it means that the rest rel-type after removing items that has been selected to show in filter panel
    filterSource: Array<any> = [];
    totalFilterSource: Array<any> = [];  // it means that all the filter source when init this dialog
    isApplying = false;
    viewContainer: ViewContainerRef;

    private _destroySubscriptions: Array<Subscription> = [];
    private _dataItemSource: any[] = [];
    private _totalRelationshipTypeList = [];
    private _isDirty = false;
    private _noData = 'No relationship type available';
    private _eventKey: string;
    private _eventValue: string;

    constructor(
        private _addRelEntityDialogService: AddRelationshipEntityDialogService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _alert: AlertWindowService,
        private _relationshipService: RelationshipService,
        private _toast: ToastService,
    ) {
    }

    ngOnInit() {
        this._isDirty = false;
        this._destroySubscriptions.push(
            this._addRelEntityDialogService.openDialog$.subscribe(res => {
                this.isReplaceWithCreatedEntity = res.payload.dialogCheckboxConfig.isReplaceWithCreatedEntity;
                this.isCreatWithReciprocalRel = res.payload.dialogCheckboxConfig.isCreatWithReciprocalRel;
                this.isCreatWithReciprocalRelShow = res.payload.dialogCheckboxConfig.isCreatWithReciprocalRelShow;
                this.isReplaceExistingShow = res.payload.dialogCheckboxConfig.isReplaceExistingShow;
                this._isDirty = false;
                if (res && res.type === 'openDialog') {
                    this.rootNode = res.payload.rootNode;
                    this.dialogTitle = res.payload.dialogTitle;
                    this.operation = res.payload.operation;
                    this._eventKey = res.payload.eventKey;
                    this._eventValue = res.payload.eventValue;
                    this.filterPanelList = [];
                    if (this.operation === 'Add Contacts') {
                        this.metadataType = FilterMetadataType.FlyContact;
                    } else {
                        this.metadataType = FilterMetadataType.FlyEntity;
                    }
                    this.addEntityPanelConfig = {
                        'currentRelationshipType': res.payload.currentRelationshipType,
                        'validRelationshipType': res.payload.selectedItemValidRelationshipType,
                        'rootNode': this.rootNode,
                        'totalValidRelationshipType': res.payload.totalValidRelationshipType
                    };
                    this._initFilterPanel();
                    this._initFilterValidRelTypes();

                    this.dialogOpened = true;
                } else {
                    this.dialogOpened = false;
                }
            }),
            this.filterConfig.events$.subscribe(event => this._filterPanelEventHandler(event)),

        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    apply() {
        const filterList = this.filterPanelList.filter(item => item.value.length > 0);
        if (filterList.length > 0) {
            this.isApplying = true;
            const relationshipTypes = filterList.map(item => item.fields).join(',');
            const entities = filterList.map(item => item.value.map(element => element.id).join(',')).join(';');

            const addEntityObj = {};
            addEntityObj['relType'] = relationshipTypes;
            addEntityObj['parentEntity'] = this.rootNode.id;
            addEntityObj['childEntity'] = entities;
            addEntityObj['isCreateReciprocal'] = this.isCreatWithReciprocalRel;
            addEntityObj['isAppend'] = !this.isReplaceWithCreatedEntity;

            this._relationshipService.createMultipleRelationship(addEntityObj).pipe(
                catchError((err) => {
                    this._toast.notify({
                        message: err.message,
                        style: NotificationStyles.Error
                    });
                    return of(null);
                })
            ).subscribe(data => {
                this.isApplying = false;
                this.dialogOpened = false;
                this._addRelEntityDialogService.addEntityToRelationship$.next({
                    type: RelationshipTreeEvents.ADD_ENTITY,
                    payload: {
                        'relationshipTypes': relationshipTypes,
                        'serverResponse': data,
                    }
                });
            });
        } else {
            // if not modified, will close the dialog directly
            this.dialogOpened = false;
        }
    }

    cancel() {
        if (this._isDirty) {
            this._alert.warn('You have unsaved changes', ['Are you sure you want to discard all your changes?'], 'Discard', 'Go Back', this._eventKey, this._eventValue).pipe(
                take(1),
            ).subscribe(result => {
                if (result) {
                    this.dialogOpened = false;
                    this._addRelEntityDialogService.addEntityToRelationship$.next(DialogAction.CloseDialog);
                } else {
                    this.dialogOpened = true;
                }
            });
        } else {
            this.dialogOpened = false;
            this._addRelEntityDialogService.addEntityToRelationship$.next(DialogAction.CloseDialog);
        }
    }

    onClickSetEventValue(e: Event) {
        if (this._eventKey && this._eventValue) {
            e[this._eventKey] = this._eventValue;
        }
    }

    onContainerEvent(event) {
        this.viewContainer = event;
    }

    // add the entity to relationship type input box
    private _filterPanelEventHandler(event) {
        this._isDirty = true;
        if (event.type === FilterPanelEvents.change || event.type === FilterPanelEvents.delete) {
            this.filterPanelList = [];
            this.filterPanelList = event.payload;
            if (event.type === FilterPanelEvents.delete) {
                this.filterSource = [];
                this.filterSource = this.totalFilterSource.filter(item => {
                    return this.filterPanelList.every(element => {
                        return element.fields !== item.valueField;
                    });
                });
                if (this.filterSource.length === 0) {
                    this.filterSource.push({ textField: this._noData });
                }
            }
        }
    }

    // when select one rel-type, will add this item to filter panel
    private _filterValueChange(textField, valueField, source) {
        this._isDirty = true;
        const _filtersConfig = this._addRelEntityDialogService.getFilterConfig(textField, valueField, source, this.metadataType,
            this.rootNode, this.viewContainer);
        this.filterConfig.action$.next({
            type: FilterPanelActions.addFilter,
            payload: [_filtersConfig]
        });
        this.filterSource = this.filterSource.filter(item => item.valueField !== _filtersConfig.fields);
        if (this.filterSource.length === 0) {
            this.filterSource.push({ textField: this._noData });
        }
    }

    private _initFilterPanel() {
        this._totalRelationshipTypeList = this.addEntityPanelConfig.totalValidRelationshipType;
        this.filterConfig.filtersConfigList = [];
        // when user click on one rel-type, this type should show in filter panel at the beginning
        if (this.addEntityPanelConfig.currentRelationshipType) {
            const relationshipType = this.addEntityPanelConfig.currentRelationshipType;
            this._filterRelationshipType(relationshipType.id);
            const _filtersConfig = this._addRelEntityDialogService.getFilterConfig(relationshipType.name,
                relationshipType.id, this._dataItemSource, this.metadataType, this.rootNode, 'component');
            this.filterConfig.filtersConfigList.push(_filtersConfig);
            this._isDirty = false;
        }
    }

    private _filterRelationshipType(relationshipTypeId) {
        this._isDirty = true;
        const rootNodeTypeId = this.rootNode.type.id;
        this._dataItemSource = [];

        if (this.operation === 'Add Contacts') {
            // add contact
            this._totalRelationshipTypeList.forEach(ele => {
                if (ele.childEntityType.id === EntityType.CONTACT.id && ele.parentEntityType.id === EntityType.CONTACT.id) {
                    this._dataItemSource.push(ele.childEntityType.id);
                }
            });
        } else {
            // add entity
            if (rootNodeTypeId !== EntityType.CONTACT.id && rootNodeTypeId !== EntityType.TEAM.id
                && rootNodeTypeId !== EntityType.USER.id) {
                // if the rootNode is an entity: need to filter according to parent and current child rel-type
                this._totalRelationshipTypeList.forEach(ele => {
                    if (ele.relationshipType.id === relationshipTypeId && ele.parentEntityType.id === rootNodeTypeId) {
                        this._dataItemSource.push(ele.childEntityType.id);
                    }
                });
            } else {
                // if the rootNode is a contact: need to add all the entity type
                this._totalRelationshipTypeList.forEach(ele => {
                    const childCondition = ele.childEntityType.id !== EntityType.CONTACT.id && ele.childEntityType.id !== EntityType.USER.id;
                    const parentCondition = ele.parentEntityType.id !== EntityType.CONTACT.id && ele.parentEntityType.id !== EntityType.USER.id;
                    if (childCondition && parentCondition) {
                        this._dataItemSource.push(ele.childEntityType.id);
                    }
                });
            }
        }
    }
    // to prepare the valid rel-type data and set kendo dropdown button item
    private _initFilterValidRelTypes() {
        this.totalFilterSource = [];
        this.addEntityPanelConfig.validRelationshipType.forEach(element => {
            this.totalFilterSource.push({
                textField: element.caption,
                valueField: element.id,
                click: (dataItem) => {
                    this._filterRelationshipType(dataItem.valueField);
                    this._filterValueChange(dataItem.textField, dataItem.valueField, this._dataItemSource);
                }
            });
        });
        ArrayHelperService.sort(this.totalFilterSource, 'textField');
        this.filterSource = this.totalFilterSource;
        if (this.addEntityPanelConfig.currentRelationshipType) {
            // need to remove the default rel-type
            this.filterSource = this.filterSource.filter(item =>
                item.valueField !== this.filterConfig.filtersConfigList[0].fields);
        }
        if (this.filterSource.length === 0) {
            this.filterSource.push({ textField: this._noData });
        }
        this._changeDetectorRef.detectChanges();
    }
}
